import { FC, useEffect } from 'react';
import { Box, DialogContent } from '@mui/material';
import { useMeasure } from 'react-use';
import { useQueryClient } from '@tanstack/react-query';
import { PinturaDefaultImageWriterResult } from '@pqina/pintura';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import { QueryKeys } from '@config';
import { ViewNavigationBar } from '@components';
import { useCreatePhotoArtwork } from '@core/features/artworks';
import { useLoadingDialog } from '@hooks';
import { useBasketStore, useBasketLimit } from '@customer/features/basket';
import { EditorMenu } from './photo-artwork-editor';
import { PhotoArtworkEditor } from './photo-artwork-editor';
import { useEditorStore } from './photo-artwork-editor/EditorStore';

import { fileToDataURL } from '@utils';

type Props = {
  onClose: (confirmClose?: boolean) => void;
};

export const EditPhotoStep: FC<Props> = ({ onClose }) => {
  const [dialog, handleProgress] = useLoadingDialog();
  const { editor, resetStore, decoration } = useEditorStore();
  const [boxRef, { height }] = useMeasure();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const addPosition = useBasketStore((state) => state.addPosition);
  const { maxOrderQuantity } = useBasketLimit();

  const { mutateAsync } = useCreatePhotoArtwork(handleProgress);

  useEffect(() => {
    // reset the editor store when the dialog is closed
    return () => {
      resetStore();
    };
  }, []);

  const handleProcess = () => {
    editor?.processImage().then((data: PinturaDefaultImageWriterResult) => {
      fileToDataURL(data.dest).then(async (dataURL) => {
        try {
          const resData = await mutateAsync({
            cropped_image_state: data.imageState,
            cropped_image: dataURL as string,
            decorations: decoration ? [decoration.id] : [],
          });
          queryClient.setQueryData([QueryKeys.ARTWORKS, resData.id], resData);
          if (addPosition(resData.id, maxOrderQuantity)) {
            toast.success(t('basketAddedToCart'));
          } else {
            toast.error(t('basketLimitReached', { limit: maxOrderQuantity }));
          }
          onClose(false);
        } catch (error) {
          console.error('Error creating artwork', error);
        }
      });
    });
  };

  return (
    <>
      <ViewNavigationBar
        onClose={() => onClose()}
        title="Deine Kreation"
        onNext={handleProcess}
        nextTitle="Weiter"
      />
      {/* <EditorSubMenu /> */}
      <DialogContent
        ref={boxRef}
        sx={{
          position: 'relative',
          height: '100%',
          minHeight: {
            md: 'calc(100vh - 400px)',
          },
          padding: 0,
          backgroundColor: '#ECF1FC',
        }}
      >
        <Box sx={{ height: height }}>
          <PhotoArtworkEditor />
        </Box>
      </DialogContent>
      <EditorMenu />
      {dialog}
    </>
  );
};
